import {Component, DestroyRef, EventEmitter, inject, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ConfirmationDialogComponent, FrameEntityHttpService, FrameEntityTableComponent, getMe, HasPermissionPipe, SnackBarService, State, UserModel} from '@overflo1/frame-frontend';
import {BaseForm, ControlTypesEnum, DynamicFormComponent} from '@overflo1/dynamic-form';
import {ColumnModel, FullTableActionModel, FullTableComponent} from '@overflo1/full-table';
import {MatTabsModule} from '@angular/material/tabs';
import {QueryJoin, SCondition} from "@nestjsx/crud-request";
import {MachineModel} from '../../models/machine.model';
import {MachineProfileOptionModel} from '../../models/machine-profile-option.model';
import {MachineTranslations} from 'src/app/frame-entity-config/machine';
import {MatButtonModule} from '@angular/material/button';
import {MachineService} from '../../services/machine/machine.service';
import {MatSelectModule} from '@angular/material/select';
import {AsyncPipe, DatePipe, Location, NgIf} from '@angular/common';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {firstValueFrom, Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatIconModule} from '@angular/material/icon';
import {MachineRequestModel} from '../../models/machine-request.model';
import {RequestTranslations} from 'src/app/frame-entity-config/request';
import {environment} from 'src/environments/environment';
import {INSTALLATION_TYPES, InstallationType} from '../../models/installation-type.type';
import {ORDERED_WEEK_DAYS} from '../../enums/week-days.enum';
import {AlmaboxPermissions} from 'src/app/almabox.permissions';
import {MachineSchedulerComponent} from '../machine-scheduler/machine-scheduler.component';
import {SchedulerConfigModel} from '../../models/scheduler-config.model';
import isEqual from 'lodash/isEqual';
import {MatTooltipModule} from '@angular/material/tooltip';
import {FamilyService} from "../../../family/services/family.service";
import {FamilyModel} from "../../../family/models/family.model";
import {MachineCommandsComponent} from "../machine-commands/machine-commands.component";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Store} from "@ngrx/store";
import {MachineParamsField} from '../../enums/machine-params-field.enum';
import {MatDialog} from '@angular/material/dialog';

@Component({
  selector: 'app-machine-detail',
  standalone: true,
  imports: [
    NgIf,
    AsyncPipe,
    FrameEntityTableComponent,
    MatTabsModule,
    DynamicFormComponent,
    MatButtonModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatIconModule,
    HasPermissionPipe,
    MachineSchedulerComponent,
    MatTooltipModule,
    MachineCommandsComponent,
    FullTableComponent
  ],
  providers: [],
  templateUrl: './machine-detail.component.html',
  styleUrl: './machine-detail.component.scss'
})
export class MachineDetailComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  readonly AlmaboxPermissions = AlmaboxPermissions;
  readonly ControlTypesEnum = ControlTypesEnum;
  readonly MachineTranslations = MachineTranslations;
  loggedUser?: UserModel;
  entityName: string = 'machine';
  entityId: number;
  requestTableSearch: SCondition;
  requestTableJoin: QueryJoin[] = [{field: 'machine'}];
  forms: BaseForm[] = [];
  machine: MachineModel;
  submitting: boolean = false;
  @ViewChild(DynamicFormComponent) dynamicFormComponent: DynamicFormComponent;
  machineGroups: string[];
  groupControl: FormControl;
  filteredGroups: Observable<string[]>;
  requestTableColumnList: ColumnModel[];
  @ViewChild(MachineSchedulerComponent) machineSchedulerComponent: MachineSchedulerComponent;
  @ViewChild(MachineCommandsComponent) machineCommandsComponent: MachineCommandsComponent;
  originalSchedulerConfigEnabled: boolean;
  originalSchedulerConfig: SchedulerConfigModel;

  get installationType(): InstallationType {
    const value = this.machine?.schedulerConfig?.installation_type;
    return value && INSTALLATION_TYPES.includes(value) ? value : 'projector';
  }

  set installationType(value: InstallationType) {
    if (!this.machine?.schedulerConfig) {
      return;
    }
    this.machine.schedulerConfig.installation_type = value;
  }

  slimMachines: Partial<MachineModel>[] = [];
  currentAssociationIds: number[] = [];

  // Associated machines table
  machineTableColumnList: ColumnModel[] = []
  machineTableSearch: SCondition = {};
  machineTableJoin: QueryJoin[] = [{field: 'lastRequest'}, {field: 'family'}]
  machineTableRefreshEmitter = new EventEmitter<void>();
  machineTableQuickSearchColumns: string[] = ['description', 'uid',];
  machineTableActions = new EventEmitter<void | FullTableActionModel>();

  constructor(
    private route: ActivatedRoute,
    private frameEntityHttpService: FrameEntityHttpService<MachineModel>,
    private familyService: FamilyService,
    private snackBarService: SnackBarService,
    private router: Router,
    private machineService: MachineService,
    private hasPermissionPipe: HasPermissionPipe,
    private datePipe: DatePipe,
    private location: Location,
    private store: Store<State>,
    private matDialog: MatDialog,
  ) {
  }

  async ngOnInit() {
    await this.prepareMachineTableColumnList();
    await this.prepareRequestTableColumnList();
    this.store.select(getMe).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => this.loggedUser = user)
    this.route.params.subscribe(async (params) => {
      const entityId = params?.['entityId'];
      if (entityId && entityId !== 'create') {
        this.entityId = entityId;

        this.requestTableSearch = {
          'machine.id': {$eq: this.entityId},
        };

        await this.loadMachineData();
        this.populateOriginalScheduler();
      }
      await this.loadForms();

      this.machineTableActions.subscribe(async (x: FullTableActionModel | void) => {
        if (x) {
          switch (x.type) {
            case 'view':
              await this.router.navigate(['frame/entity/machine/' + x.element.id.toString()]);
              break;
          }
        }
      });
    });
  }

  private async loadMachineData(): Promise<void> {
    const [machine, slimMachines, associations] = await Promise.all([
      this.frameEntityHttpService.getOne(this.entityName, this.entityId, 'include_deleted=1'),
      this.machineService.getSlimMachines(),
      this.machineService.getAssociations(Number(this.entityId)),
    ]);
    this.machine = machine;
    const hasWeekSkeleton = ORDERED_WEEK_DAYS.some((day) => !!this.machine.schedulerConfig?.[day]);
    if (!hasWeekSkeleton) {
      const installationType = this.machine.schedulerConfig?.installation_type;
      this.machine.schedulerConfig = new SchedulerConfigModel();
      if (installationType) {
        this.machine.schedulerConfig.installation_type = installationType;
      }
    }
    this.slimMachines = slimMachines;
    this.currentAssociationIds = associations.map((m) => m.id);
    this.requestTableSearch = {'machineId': {$in: [Number(this.entityId), ...this.currentAssociationIds]}};
    this.machineTableSearch = {'id': {$in: this.currentAssociationIds}};
  }

  async prepareRequestTableColumnList() {
    const columns = [
      {
        def: "createdAt",
        name: RequestTranslations.fields.createdAt,
        value: (element: MachineRequestModel) => this.datePipe.transform(element.createdAt, environment.DATE_TIME_FORMAT, undefined, environment.LOCALE),
        sort: true,
        type: "date",
      },
      {
        def: "uid",
        name: RequestTranslations.fields.uid,
        value: (element: MachineRequestModel) => element.uid,
        sort: true,
        type: "string",
      }, {
        def: "machine.description",
        name: MachineTranslations.fields.description,
        value: (element: MachineRequestModel) => element.machine?.description ?? '',
        sort: true,
        type: "string",
      },
      {
        def: "ipAddress",
        name: RequestTranslations.fields.ipAddress,
        value: (element: MachineRequestModel) => element.ipAddress,
        sort: true,
        type: "string",
      }
    ];

    this.requestTableColumnList = columns;
  }

  async prepareMachineTableColumnList() {
    const actionsToView: FullTableActionModel[] = [{
      type: 'view',
      icon: 'visibility',
    }]
    this.machineTableColumnList = [
      {
        def: "description",
        name: MachineTranslations.fields.description,
        value: (element: MachineModel) => element.description ?? '',
        sort: true,
        type: "string",
      },
      {
        def: "family.name",
        name: MachineTranslations.fields.family,
        value: (element: MachineModel) => element.family?.enabled ? element.family.name : '',
        sort: false,
        type: 'string',
      },
      {
        def: "uid",
        name: MachineTranslations.fields.uid,
        value: (element: MachineModel) => element.uid,
        sort: true,
        type: 'string',
      },
      {
        def: "lastRequest.createdAt",
        name: "Ultima richiesta",
        value: (element: MachineModel) => element.lastRequest ? new DatePipe(environment.DATE_LOCALE).transform(
          element.lastRequest.createdAt,
          environment.DATE_TIME_FORMAT
        ) : '-',
        sort: true,
        type: 'date',
      },
      {
        def: 'actions',
        name: 'Visualizza',
        type: 'string',
        value: actionsToView,
        width: 80
      }
    ];
  }

  async loadMachineGroups() {
    this.machineGroups = await this.machineService.getMachineGroups();
    this.groupControl = new FormControl(this.machine?.group || '');
    if (this.isMachineDeleted()) {
      this.groupControl.disable();
    }

    this.filteredGroups = this.groupControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || '')),
    );
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.machineGroups.filter(option =>
      option &&
      option.toLowerCase().includes(filterValue)
    );
  }

  async loadForms() {
    await this.loadMachineGroups();

    const families: FamilyModel[] = await this.familyService.getAllFamilies();
    if (!families) {
      this.snackBarService.openErrorSnackBar('Nessuna famiglia trovata')
      throw new Error('No family found.');
    }
    const familyOptions = families.filter(f => f.enabled).map((family) => {
      return {
        key: family.name,
        value: family.id,
        mnemonicId: family.name,
        description: ''
      };
    });

    const profilesList: MachineProfileOptionModel[] = await this.machineService.getAllProfiles();
    const profilesOptions = profilesList.map((profile) => {
      return {
        key: profile.name,
        value: profile.name,
        mnemonicId: profile.name,
        description: ''
      };
    });

    this.forms = [];

    const isDeleted = this.isMachineDeleted();

    const forms = [
      new BaseForm({
        key: 'uid',
        value: this.machine ? this.machine.uid : null,
        label: MachineTranslations.fields.uid,
        controlType: 'text',
        required: false,
        readonly: isDeleted,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'description',
        value: this.machine ? this.machine.description : null,
        label: MachineTranslations.fields.description,
        controlType: 'text',
        required: false,
        readonly: isDeleted,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'family',
        value: this.machine?.family?.enabled ? this.machine.family.id : null,
        label: MachineTranslations.fields.family,
        controlType: 'select',
        options: familyOptions,
        required: true,
        readonly: isDeleted,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'profile',
        value: this.getValueFromParams(MachineParamsField.Profile),
        label: MachineTranslations.fields.profile,
        controlType: 'select',
        options: profilesOptions,
        required: false,
        readonly: isDeleted,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'games',
        value: this.getValueFromParams(MachineParamsField.Games),
        label: MachineTranslations.fields.games,
        controlType: 'number',
        required: false,
        readonly: isDeleted,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'params',
        value: this.machine ? this.getParamsWithoutVirtualFields() : null,
        label: MachineTranslations.fields.params,
        controlType: 'text',
        required: false,
        readonly: isDeleted,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'lampHours',
        value: this.machine ? this.machine.lampHours : null,
        label: MachineTranslations.fields.lampHours,
        controlType: 'text',
        type: 'number',
        required: false,
        readonly: true,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'projector',
        value: this.machine ? this.machine.projector : null,
        label: MachineTranslations.fields.projector,
        controlType: 'text',
        required: false,
        readonly: isDeleted,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'enabled',
        value: this.machine ? this.machine['enabled'] : false,
        label: MachineTranslations.fields.enabled,
        controlType: 'checkbox',
        required: false,
        readonly: isDeleted,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'lampHoursEmailEnabled',
        value: this.machine ? this.machine['lampHoursEmailEnabled'] : false,
        label: MachineTranslations.fields.lampHoursEmailEnabled,
        controlType: 'checkbox',
        required: false,
        readonly: isDeleted,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'ipAddress',
        value: this.machine ? this.machine['ipAddress'] : '',
        label: MachineTranslations.fields.ipAddress,
        controlType: 'text',
        required: false,
        readonly: true,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'macAddress',
        value: this.machine ? this.machine['macAddress'] : '',
        label: MachineTranslations.fields.macAddress,
        controlType: 'text',
        required: false,
        readonly: true,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'networkInfoLastUpdate',
        value: this.machine ? (this.datePipe.transform(this.machine['networkInfoLastUpdate'], 'dd/MM/yyyy HH:mm') ?? '') : '',
        label: MachineTranslations.fields.networkInfoLastUpdate,
        controlType: 'text',
        required: false,
        readonly: true,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'deletedAt',
        value: this.machine?.deletedAt ? (this.datePipe.transform(this.machine.deletedAt, 'dd/MM/yyyy HH:mm') ?? '') : '',
        label: MachineTranslations.fields.deletedAt,
        if: (elem)=> !!(elem.deletedAt),
        controlType: 'text',
        required: false,
        readonly: true,
        fullWidth: false,
      }),
      new BaseForm({
        key: 'associatedMachines',
        value: this.currentAssociationIds,
        label: MachineTranslations.fields.associatedMachines,
        controlType: 'multipleselect',
        required: false,
        options: this.slimMachines
          .filter((m) => m.id !== Number(this.entityId))
          .map((m) => ({key: `${m.description} - ${m.uid}`, value: m.id, mnemonicId: `${m.description} - ${m.uid}`, description: ''})),
        readonly: isDeleted,
        fullWidth: false,
      })
    ];

    this.forms = this.forms.concat(forms);
  }

  getValueFromParams(field: MachineParamsField): string | null {
    if (!this.machine?.params) return null;
    const params = new URLSearchParams(this.machine.params);
    return params.get(field);
  }

  private getParamsWithoutVirtualFields(): string | null {
    if (!this.machine?.params) return null;
    const params = new URLSearchParams(this.machine.params);
    params.delete(MachineParamsField.Profile);
    params.delete(MachineParamsField.Games);
    return params.toString() || null;
  }

  private mergeParamsFields(data: Partial<MachineModel>): void {
    const existingParams = new URLSearchParams(data.params ?? '');
    if (data.profile) {
      existingParams.set(MachineParamsField.Profile, data.profile);
    } else {
      existingParams.delete(MachineParamsField.Profile);
    }
    if (data.games) {
      existingParams.set(MachineParamsField.Games, String(Number(data.games)));
    } else {
      existingParams.delete(MachineParamsField.Games);
    }
    data.params = existingParams.toString();
    delete data.profile;
    delete data.games;
  }

  isMachineDeleted(): boolean {
    return !!this.machine?.deletedAt;
  }

  confirm() {
    this.submitting = true;
    this.dynamicFormComponent.onSubmit();
    this.submitting = false;
  }

  async save(formResult: Partial<MachineModel>) {
    if (!this.submitting) {
      return;
    }

    const group = this.groupControl.value || '';

    const machineId = this.machine?.id as number;

    const data: Partial<MachineModel> = {
      id: machineId,
      uid: formResult.uid,
      description: formResult.description,
      family: formResult.family,
      games: formResult.games,
      profile: formResult.profile,
      params: formResult.params,
      group: group,
      projector: formResult.projector,
      enabled: formResult['enabled'],
      lampHoursEmailEnabled: formResult['lampHoursEmailEnabled'],
      version: this.machine?.['version'],
    };

    this.mergeParamsFields(data);

    if (this.machine) {
      data.schedulerConfig = {
        ...this.originalSchedulerConfig,
        installation_type: this.installationType,
      };
      try {
        await this.frameEntityHttpService.updateOne(this.entityName, data as MachineModel);
      } catch {
        this.snackBarService.openErrorSnackBar("Errore nell'aggiornamento della macchina");
        return;
      }

      if (this.entityId) {
        const raw = formResult['associatedMachines'];
        const selectedIds: number[] = Array.isArray(raw)
          ? raw.filter((v): v is number => typeof v === 'number')
          : [];
        try {
          await this.machineService.updateAssociations(Number(this.entityId), selectedIds);
        } catch {
          this.snackBarService.openErrorSnackBar("Errore nell'aggiornamento delle associazioni");
        }
      }

      await this.loadMachineData();
      await this.loadForms();
      this.snackBarService.openInfoSnackBar(`Macchina aggiornata con successo`);

      if (this.machine.schedulerEnabled && this.hasInstallationTypeChanged()) {
        await this.machineService.sendCommand({
          machineId: Number(this.entityId),
          command: 'reloadScheduler',
          sentBy: this.loggedUser?.email ?? '',
        });
        await this.machineCommandsComponent?.loadCommandsHistory();
      }
      this.populateOriginalScheduler();
    } else {
      const duplicate = await this.findExistingMachineByUid(data.uid);
      if (duplicate) {
        await this.showDuplicateUidDialog(duplicate);
        return;
      }
      try {
        this.machine = await this.frameEntityHttpService.createOne(this.entityName, data as MachineModel);
      } catch {
        this.snackBarService.openErrorSnackBar('Errore nella creazione della macchina');
        return;
      }
      this.snackBarService.openInfoSnackBar(`Macchina creata con successo`);
      this.loadForms();
    }

    this.entityId = this.machine?.id;
    await this.router.navigate([`/frame/entity/machine/${this.machine?.id}`]);
  }

  private async findExistingMachineByUid(uid: string): Promise<Partial<MachineModel> | undefined> {
    const existing = await this.machineService.getSlimMachines();
    return existing.find((m) => m.uid?.toLowerCase() === uid?.toLowerCase());
  }

  private async showDuplicateUidDialog(match: Partial<MachineModel>): Promise<void> {
    const statusHint = match.deletedAt ? ' (attualmente eliminata)' : '';
    const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Macchina già esistente',
        description: `È già presente una macchina con UID "${match.uid}"${statusHint} e nome ${match.description}. Vuoi visualizzarne la scheda?`,
      },
    });
    const goToMachine = await firstValueFrom(dialogRef.afterClosed());
    if (goToMachine) {
      await this.router.navigate(['/frame/entity/machine', match.id]);
    }
  }

  async saveSchedulerConfig() {
    const valid = this.machineSchedulerComponent.validateSchedulerConfig();
    if (!valid) {
      return;
    }
    const schedulerConfigResult = this.machineSchedulerComponent.get();
    if (this.machine) {
      try {
        await this.frameEntityHttpService.updateOne(this.entityName, {
          ...this.machine,
          schedulerEnabled: schedulerConfigResult.schedulerEnabled,
          schedulerConfig: schedulerConfigResult.schedulerConfig,
        });
      } catch {
        this.snackBarService.openErrorSnackBar("Errore nell'aggiornamento dello scheduler");
        return;
      }
      this.machine = await this.frameEntityHttpService.getOne(this.entityName, this.entityId);
      this.snackBarService.openInfoSnackBar(`Scheduler aggiornato con successo`);
      await this.machineService.sendCommand({
        machineId: Number(this.entityId),
        command: 'reloadScheduler',
        sentBy: this.loggedUser?.email ?? '',
      })
      await this.machineCommandsComponent?.loadCommandsHistory();
      this.populateOriginalScheduler();

    }
  }

  async restore(): Promise<void> {
    await this.machineService.restoreMachine(this.machine.id);
    this.snackBarService.openInfoSnackBar('Macchina ripristinata con successo');
    await this.loadMachineData();
    await this.loadForms();
  }

  populateOriginalScheduler() {
    if (!this.machine?.schedulerConfig) {
      return;
    }
    this.originalSchedulerConfig = JSON.parse(JSON.stringify(this.machine.schedulerConfig));
    this.originalSchedulerConfigEnabled = this.machine.schedulerEnabled;
  }

  async downloadSchedulerConfig() {
    const config = this.machine.schedulerConfig;
    const json = JSON.stringify(config, null, 2);
    const blob = new Blob([json], {type: 'application/json'});
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `scheduler-config-${this.machine.uid || this.machine.id}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);
  }

  hasSchedulerConfigChanged(): boolean {
    return !this.originalSchedulerConfig ||
      this.machine.schedulerEnabled !== this.originalSchedulerConfigEnabled ||
      !isEqual(this.machine.schedulerConfig, this.originalSchedulerConfig);
  }

  hasInstallationTypeChanged(): boolean {
    const originalValue = this.originalSchedulerConfig?.installation_type;
    const originalNormalized: InstallationType =
      originalValue && INSTALLATION_TYPES.includes(originalValue) ? originalValue : 'projector';
    return this.installationType !== originalNormalized;
  }

  goBack(): void {
    this.router.navigate(['frame/entity/machine/']);
  }
}
